<template>
  <div class="deal-card-tabs">
    <ul class="deal-card-tabs__list">
      <li
        v-for="(section, index) in sections"
        :key="`deal-card-tabs-item-${index}`"
        class="deal-card-tabs__item"
        :class="{
          'deal-card-tabs__item--active': index === activeSectionIndex,
        }"
        @click="activeSectionIndex = index"
      >
        {{ section.label }}
      </li>
    </ul>
    <div class="deal-card-tabs__sections_wrapper">
      <section
        v-for="(section, index) in sections"
        :key="`deal-card-tabs-section-${index}`"
        :class="{
          'deal-card-tabs__section--active': index === activeSectionIndex,
          'deal-card-tabs__section--sticky': section.hasStickyBar,
        }"
        :style="{ height: sectionHeight }"
        class="deal-card-tabs__section"
      >
        <CardSection
          v-for="subSection in section.subSections"
          v-show="!!subSection.cards.length || (subSection.actionLabel && subSection.actionEvent)"
          :key="subSection.name"
          :title="subSection.title"
          :action-label="subSection.actionLabel"
          :action-event="subSection.actionEvent"
          @actionCall="(event) => $emit('cardSectionEvent', event)"
        >
          <Card
            v-for="(card, cardIndex) in subSection.cards"
            :key="`deal-card-tabs-item-${cardIndex}`"
            :title="card.title"
          >
            <component
              :is="cardContentComponents[card.cardType]"
              :deal="deal"
              :card-dynamic-data="card.cardDynamicData"
            />
          </Card>
          <div v-if="section.name === 'overview'" class="deal-card-tabs__button_wrapper">
            <HdButton class="deal-card-tabs__button" @click="openConnectDealInNewTab()">
              {{ $t('DASHBOARD.CARD.EXPANDED.OVERVIEW.CTA_LABEL') }}
            </HdButton>
          </div>
        </CardSection>
      </section>
    </div>
  </div>
</template>

<script>
import { HdButton, HdIcon, OnResizeService } from 'homeday-blocks';
import { plusCircle } from 'homeday-assets';
import Card from '@/components/dashboard/Card.vue';
import { CARD_CONTENT_COMPONENTS } from '@/components/dashboard/CardContent';
import { isDesktopWide } from '@/services/breakpoints';
import CardSection from '@/components/dashboard/CardSection.vue';

export default {
  name: 'DealCardTabs',
  components: { CardSection, Card, HdButton, HdIcon },
  props: {
    sections: {
      type: Array,
      required: true,
    },
    deal: {
      type: Object,
      required: true,
    },
  },
  data() {
    let activeSectionIndex = 0;

    if (!isDesktopWide()) {
      const tabQueryParam = this.$route?.query.tab;

      if (tabQueryParam) {
        const sectionIndex = this.sections.findIndex(
          ({ name }) => name.toLowerCase() === tabQueryParam.toLowerCase(),
        );

        if (sectionIndex >= 0 && sectionIndex < this.sections.length)
          activeSectionIndex = sectionIndex;
      }
    }

    return {
      sectionHeight: '100vh',
      isMobile: !isDesktopWide(),
      activeSectionIndex,
    };
  },
  computed: {
    icons() {
      return {
        plusCircle,
      };
    },
    cardContentComponents() {
      return CARD_CONTENT_COMPONENTS;
    },
  },
  mounted() {
    this.addListeners();
    this.calculateSectionHeight();
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    isNoteSection(section) {
      return section.name === 'notes';
    },
    openConnectDealInNewTab() {
      window.open(`${process.env.VUE_APP_HD_CONNECT}/crm/deals/${this.deal.uid}`, '_blank');
    },
    async calculateSectionHeight() {
      await this.$nextTick();
      const documentHeight = `${window.innerHeight}px` ?? '100vh';
      const titleElement = this.$parent.$el.querySelector('.deal-card-expanded__title');
      const titleHeight = this.isMobile && titleElement ? titleElement?.clientHeight : 64;
      const tabsHeight = this.isMobile ? 40 : 96;
      this.sectionHeight = `calc(${documentHeight} - ${titleHeight + tabsHeight}px)`;
    },
    addListeners() {
      OnResizeService.onThrottled(this.setIsMobile);
    },
    removeListeners() {
      OnResizeService.offThrottled(this.setIsMobile);
    },
    setIsMobile() {
      this.isMobile = !isDesktopWide();
      this.calculateSectionHeight();
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/mixins.scss';
.deal-card-tabs {
  -webkit-overflow-scrolling: touch;

  &__list {
    display: flex;
    cursor: pointer;
    gap: $sp-m;
    padding: 0 $sp-m;

    @include for('desktop-wide') {
      background: $primary-bg;
      gap: 0;
      justify-content: space-between;
      padding: 0;
      cursor: unset;
    }
  }

  &__item {
    padding: $sp-m - $sp-xs $sp-s;

    @include for('tablet') {
      padding: $sp-m - $sp-xs;
    }

    @include for('desktop-wide') {
      @include font('DS-300');
      border-right: 1px solid getShade($quaternary-color, 50);
      box-shadow: 0 0 24px 0 rgba(26, 27, 60, 0.1);
      color: $primary-color;
      font-weight: 900;
      padding: $sp-l;
      pointer-events: none;
      width: 50%;
      z-index: 99;
    }

    &--active {
      background-color: $primary-bg;
      border-radius: $sp-s $sp-s 0 0;
      color: $primary-color;
      font-weight: 700;
      position: relative;

      @include for('desktop-wide') {
        border-radius: 0;
        font-weight: 900;
      }

      &:after,
      &:before {
        bottom: 0;
        content: '';
        height: $sp-m;
        position: absolute;
        width: $sp-m;

        @include for('desktop-wide') {
          display: none;
        }
      }

      &:before {
        border-bottom-right-radius: $sp-s;
        box-shadow: $sp-s 0 0 0 $primary-bg;
        left: -$sp-m;
      }

      &:after {
        border-bottom-left-radius: $sp-s;
        box-shadow: -$sp-s 0 0 0 $primary-bg;
        right: -$sp-m;
      }
    }
  }

  &__sections_wrapper {
    -webkit-overflow-scrolling: touch;

    @include for('desktop-wide') {
      display: flex;
    }
  }

  &__button_wrapper {
    background-color: $primary-bg;
    box-shadow: 0 0 24px 0 rgba(26, 27, 60, 0.16);
    margin-left: -$sp-m;
    padding: $sp-m $sp-m $sp-m + $sp-xs $sp-m;
    position: fixed;
    bottom: 0;
    width: 100%;

    @include for('tablet') {
      display: none;
    }

    button {
      width: 100%;
    }
  }

  &__section {
    background-color: $primary-bg;
    color: $primary-color;
    display: none;
    padding: $sp-m;
    width: 100vw;
    height: 100%;
    position: relative;
    z-index: 40;
    overflow-x: hidden;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;

    @include for('desktop-wide') {
      border-right: 1px solid getShade($quaternary-color, 50);
      display: flex;
      flex-direction: column;
      gap: $sp-m + $sp-s;
      padding: $sp-m + $sp-s;
      width: 50%;

      &:not(:first-child) {
        background-color: getShade($quaternary-color, 40);
      }

      .deal-card {
        background-color: $primary-bg;
      }
    }

    @include for('tablet') {
      padding: $sp-l + $sp-s;
    }

    &--active {
      display: flex;
      flex-direction: column;
      gap: $sp-m + $sp-s;
    }

    &--sticky {
      padding-bottom: $sp-xl + $sp-m;

      @include for('tablet') {
        padding-bottom: $sp-l + $sp-s;
      }
    }

    ::v-deep article:last-of-type {
      margin-bottom: $sp-m;
    }
  }

  &__notes-section,
  &__notes-section > button {
    align-items: center;
    display: flex;
    gap: $sp-s;
    justify-content: space-between;
  }

  &__notes-section > button {
    @include font('DS-100');
    background-color: transparent;
    border: none;
    color: getShade($secondary-color, 120);
    cursor: pointer;
    font-weight: 600;
    padding: 0;

    ::v-deep path {
      fill: getShade($secondary-color, 120);
    }
  }

  &__button {
    @include for('desktop-wide') {
      display: none;
    }
  }
}
</style>
